import { useEffect, useRef, useState } from "react";

/**
 * VoiceRing – TAELS silver + blue eclipse ring for a voice assistant.
 *
 * Props
 *  - size  : CSS px width/height (default 320)
 *  - state : "idle" | "listening" | "thinking" | "speaking"
 *  - level : 0..1 live audio level (mic or TTS). Optional – a natural-looking
 *            simulated level is used when omitted.
 *
 * Motion design
 *  idle      slow breathing, gradients drift in opposite directions
 *  listening ring swells and glows with the mic level (fast attack, slow release)
 *  thinking  a bright comet orbits the ring while the ring tightens slightly
 *  speaking  hair-line threads fan out, edge ripples, soft pulses radiate outward
 *  All states cross-fade; nothing ever snaps.
 */

const TAU = Math.PI * 2;
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const ease = (rate, dt) => 1 - Math.exp(-rate * dt); // frame-rate independent smoothing

/* ---------- Palette: silver + a family of blues ---------- */
const C = {
  silverHi: [244, 248, 255],
  silver: [206, 214, 228],
  ice: [190, 220, 255],
  sky: [112, 188, 255],
  azure: [58, 134, 255],
  royal: [42, 96, 240],
  cobalt: [30, 62, 205],
  navy: [18, 34, 118],
};

// [position, colour, alpha] – first and last stop match so the wrap is seamless
const WARM_BLUE = [
  [0.0, C.azure, 0.95],
  [0.08, C.sky, 0.85],
  [0.2, C.royal, 0.45],
  [0.3, C.navy, 0.15],
  [0.4, C.cobalt, 0.3],
  [0.5, C.silver, 0.72],
  [0.62, C.ice, 0.92],
  [0.75, C.silverHi, 1],
  [0.88, C.sky, 0.95],
  [1.0, C.azure, 0.95],
];

const DEEP_BLUE = [
  [0.0, C.royal, 0.9],
  [0.15, C.cobalt, 0.5],
  [0.3, C.sky, 0.8],
  [0.45, C.navy, 0.2],
  [0.6, C.azure, 0.85],
  [0.78, C.ice, 0.9],
  [0.9, C.royal, 0.8],
  [1.0, C.royal, 0.9],
];

function conic(ctx, rot, cx, cy, stops) {
  const g = ctx.createConicGradient(rot, cx, cy);
  for (const [p, [r, gg, b], a] of stops) g.addColorStop(p, `rgba(${r},${gg},${b},${a})`);
  return g;
}

// Bright head with a fading tail behind it (used while "thinking")
function cometGradient(ctx, head, cx, cy) {
  const g = ctx.createConicGradient(head, cx, cy);
  g.addColorStop(0, "rgba(120,180,255,0)");
  g.addColorStop(0.6, "rgba(64,120,255,0)");
  g.addColorStop(0.85, "rgba(90,170,255,0.55)");
  g.addColorStop(0.985, "rgba(244,248,255,1)");
  g.addColorStop(1, "rgba(244,248,255,1)");
  return g;
}

export default function VoiceRing({ size = 320, state = "idle", level }) {
  const canvasRef = useRef(null);
  const propsRef = useRef({ state, level });
  propsRef.current = { state, level };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const motion = reduced ? 0.3 : 1;

    const cx = size / 2;
    const cy = size / 2;
    const baseR = size * 0.3;
    const baseThick = size * 0.055;
    const LINES = 64;
    const STEPS = 200;

    const COS = new Float32Array(STEPS + 1);
    const SIN = new Float32Array(STEPS + 1);
    for (let i = 0; i <= STEPS; i++) {
      COS[i] = Math.cos((i / STEPS) * TAU);
      SIN[i] = Math.sin((i / STEPS) * TAU);
    }

    // animation state
    const w = { idle: 1, listening: 0, thinking: 0, speaking: 0 };
    let env = 0; // smoothed audio envelope
    let rotA = 0;
    let rotB = 0;
    let cometA = 0;
    let raf;
    let last = performance.now();
    const t0 = last;

    const draw = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = ((now - t0) / 1000) * motion;
      const { state: st, level: lv } = propsRef.current;

      /* ---- cross-fade between states ---- */
      for (const key of Object.keys(w)) w[key] += ((st === key ? 1 : 0) - w[key]) * ease(4, dt);

      /* ---- audio envelope: fast attack, slow release ---- */
      let raw = lv;
      if (raw == null) {
        if (st === "listening") {
          raw = 0.1 + 0.22 * (0.5 + 0.5 * Math.sin(t * 2.7)) * (0.5 + 0.5 * Math.sin(t * 0.8 + 1));
        } else if (st === "speaking") {
          const syllable = 0.5 + 0.5 * Math.sin(t * 9.5 + Math.sin(t * 2.1) * 2);
          const phrase = 0.5 + 0.5 * Math.sin(t * 0.9);
          raw = 0.12 + 0.7 * syllable * (0.35 + 0.65 * phrase);
        } else raw = 0;
      }
      raw = clamp(raw);
      env += (raw - env) * (raw > env ? ease(22, dt) : ease(5, dt));

      // overall activity 0..~1.3
      const act =
        w.listening * (0.25 + env * 0.9) +
        w.speaking * (0.3 + env * 1.0) +
        w.thinking * 0.35 +
        w.idle * 0.05;

      /* ---- rotations ---- */
      rotA += dt * (0.08 + act * 0.25 + w.thinking * 1.1) * motion;
      rotB -= dt * (0.12 + act * 0.3 + w.thinking * 0.6) * motion;
      cometA += dt * (TAU / 1.5) * motion;

      /* ---- geometry ---- */
      const breathe = Math.sin((t * TAU) / 6) * size * 0.007 * (w.idle + 0.4 * w.listening);
      const R =
        baseR +
        breathe +
        env * size * 0.022 * (w.listening + w.speaking * 0.8) -
        w.thinking * size * 0.01;
      const thick = baseThick * (1 + act * 0.35);
      const amp = size * (0.0025 + 0.018 * act);
      const spread = 1.2 + act * 3; // how far threads fan out in phase

      const path = (k, dy = 0) => {
        const ef = 0.25 + 0.75 * k; // inner edge stays crisp
        const ph = k * spread;
        ctx.beginPath();
        for (let i = 0; i <= STEPS; i++) {
          const a = (i / STEPS) * TAU;
          const wob =
            amp *
            ef *
            (0.55 * Math.sin(a * 2 + t * 0.9 + ph) +
              0.3 * Math.sin(a * 3 - t * 1.3 + ph * 1.3) +
              0.15 * Math.sin(a * 5 + t * 2.1 + ph * 0.7) +
              w.speaking * 0.25 * Math.sin(a * 7 - t * 3.4 + ph));
          const r = R + thick * k + wob;
          const x = cx + COS[i] * r;
          const y = cy + dy + SIN[i] * r;
          i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        ctx.closePath();
      };

      /* ---- reset + background ---- */
      ctx.globalCompositeOperation = "source-over";
      ctx.filter = "none";
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, size, size);

      const gA = conic(ctx, rotA, cx, cy, WARM_BLUE);
      const gB = conic(ctx, rotB, cx, cy, DEEP_BLUE);
      const dim = 1 - 0.35 * w.thinking;

      ctx.globalCompositeOperation = "lighter";

      // 1) wide halo – counter-rotating deep blues
      ctx.filter = `blur(${size * 0.07}px)`;
      ctx.globalAlpha = (0.3 + 0.55 * act) * dim;
      ctx.strokeStyle = gB;
      ctx.lineWidth = thick * 2.2;
      path(0.5);
      ctx.stroke();

      // 2) mid glow – silver/blue
      ctx.filter = `blur(${size * 0.02}px)`;
      ctx.globalAlpha = (0.5 + 0.4 * act) * dim;
      ctx.strokeStyle = gA;
      ctx.lineWidth = thick;
      path(0.45);
      ctx.stroke();

      // 3) hair-line threads – alternate the two gradients for a shimmering depth
      ctx.filter = "none";
      ctx.lineWidth = 0.6;
      for (let i = 0; i < LINES; i++) {
        const k = i / (LINES - 1);
        ctx.globalAlpha = (0.4 - k * 0.2) * (0.75 + act * 0.6) * dim;
        ctx.strokeStyle = i % 2 ? gB : gA;
        path(k, -thick * 0.22 * k);
        ctx.stroke();
      }

      // 4) thinking – orbiting comet
      if (w.thinking > 0.01) {
        const cg = cometGradient(ctx, cometA, cx, cy);
        ctx.strokeStyle = cg;
        ctx.globalAlpha = w.thinking;
        ctx.filter = `blur(${size * 0.03}px)`;
        ctx.lineWidth = thick * 1.6;
        path(0.45);
        ctx.stroke();
        ctx.filter = `blur(${size * 0.006}px)`;
        ctx.lineWidth = thick * 0.5;
        path(0.15);
        ctx.stroke();
      }

      // 5) speaking – soft pulses radiating outward
      if (w.speaking > 0.01) {
        ctx.strokeStyle = gB;
        ctx.filter = `blur(${size * 0.004}px)`;
        ctx.lineWidth = 1;
        for (let j = 0; j < 2; j++) {
          const p = (t * 0.6 + j * 0.5) % 1;
          ctx.globalAlpha = (1 - p) * (1 - p) * w.speaking * (0.15 + env * 0.5);
          ctx.beginPath();
          ctx.arc(cx, cy, R + thick + p * size * 0.1, 0, TAU);
          ctx.stroke();
        }
      }

      /* ---- pure black centre ---- */
      ctx.filter = "none";
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#000";
      path(0);
      ctx.fill();

      /* ---- crisp silver-blue inner edge ---- */
      ctx.shadowColor = "rgba(130,175,255,0.9)";
      ctx.shadowBlur = size * (0.012 + env * 0.01);
      ctx.globalAlpha = 0.85 + env * 0.15;
      ctx.strokeStyle = "#e8eefc";
      ctx.lineWidth = Math.max(1, size * 0.0035);
      path(0);
      ctx.stroke();
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [size]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size, background: "#000", display: "block" }}
      role="img"
      aria-label={`TAELS voice assistant ${state}`}
    />
  );
}

/* ---------- Optional: live microphone level ---------- */
export function useMicLevel(enabled) {
  const ref = useRef(0);
  useEffect(() => {
    if (!enabled) return;
    let ctx, stream, raf;
    (async () => {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      ctx = new AudioContext();
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteFrequencyData(buf);
        ref.current = Math.min(1, buf.reduce((a, b) => a + b, 0) / buf.length / 90);
        raf = requestAnimationFrame(tick);
      };
      tick();
    })();
    return () => {
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close();
    };
  }, [enabled]);
  return ref;
}

/* ---------- Quick preview: switch states with buttons ---------- */
export function VoiceRingDemo() {
  const [state, setState] = useState("idle");
  return (
    <div style={{ background: "#000", minHeight: "100vh", display: "grid", placeItems: "center", gap: 24, padding: 24 }}>
      <VoiceRing size={360} state={state} />
      <div style={{ display: "flex", gap: 8 }}>
        {["idle", "listening", "thinking", "speaking"].map((s) => (
          <button
            key={s}
            onClick={() => setState(s)}
            style={{
              padding: "8px 16px",
              borderRadius: 999,
              border: "1px solid #2a3350",
              background: state === s ? "#cfd7e8" : "transparent",
              color: state === s ? "#05070d" : "#cfd7e8",
              cursor: "pointer",
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}